import { useEffect, useState } from 'react';

import { DeleteOutlined as DeleteOutlinedIcon, Edit as EditIcon } from '@mui/icons-material';
import { HttpStatusCode } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { ConfirmationDialog, FormDialog, ResourceNotFoundFallback } from '~components';
import { useSnackbar } from '~hooks';
import { DetailsGridLayout, usePageLayoutProperties } from '~layouts';

import AccessDevicesCard from '../components/cards/accessDevicesCard';
import AccessDeviceGroupGeneralInfoCard from '../components/cards/deviceGroupGeneralInfoCard';
import SkcDeviceGroupForm from '../forms/skcDeviceGroupForm/skcDeviceGroupForm';
import { SkcDeviceGroupNew } from '../interfaces/skcDeviceGroup';
import SkcDeviceGroupsService from '../services/skcDeviceGroupsService';

const service = new SkcDeviceGroupsService();

interface UserAction {
	type: 'delete' | 'edit';
	label?: string;
	id: string;
}

interface SkcDeviceGroupDetailsContainerProps {
	id: string;
}

const SkcDeviceGroupDetailsContainer = ({ id }: SkcDeviceGroupDetailsContainerProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { enqueueSuccessSnackbar, enqueueAxiosErrorSnackbar } = useSnackbar();
	const { setPageTitle, setPageHeaderButtons: setPageButtons } = usePageLayoutProperties();

	const [userAction, setUserAction] = useState<UserAction | null>(null);

	const { data, error } = useSWR([service.basePath, id], ([_, args]) =>
		service.getDeviceGroupById(args)
	);

	const { trigger: triggerEdit, isMutating: isEditMutating } = useSWRMutation(
		[service.basePath, id],
		([_, id], { arg }: { arg: SkcDeviceGroupNew }) =>
			service.updateCompleteDeviceGroup(id, arg, data),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(
					`${t('ui.success.message.updated')} ${t('deviceGroup').toLowerCase()}`
				);
				setUserAction(null);
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const { trigger: triggerDelete, isMutating: isDeleteMutating } = useSWRMutation(
		[service.basePath, id],
		([_, id]) => service.deleteDeviceGroup(id),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(`${t('ui.successfullyDeleted')} ${t('deviceGroup').toLowerCase()}`);
				navigate('..', { relative: 'path' });
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	useEffect(() => {
		setPageButtons([
			{
				children: t('ui.edit'),
				startIcon: <EditIcon />,
				onClick: () => setUserAction({ type: 'edit', id: id }),
			},
			{
				children: t('ui.delete'),
				color: 'error',
				startIcon: <DeleteOutlinedIcon />,
				onClick: () => setUserAction({ type: 'delete', id: id }),
			},
		]);
	}, [id, setPageButtons, t]);

	useEffect(() => {
		if (data) {
			setPageTitle(data.label);
		}
	}, [data, setPageTitle]);

	if (error?.status === HttpStatusCode.NotFound) {
		return <ResourceNotFoundFallback label={id} type={t('deviceGroup')} />;
	}

	return (
		<>
			<DetailsGridLayout>
				<AccessDeviceGroupGeneralInfoCard id={id} />
				<AccessDevicesCard id={id} />
			</DetailsGridLayout>
			<FormDialog
				title={t('editResource', { resource: t('deviceGroup') })}
				open={userAction?.type === 'edit'}
				loading={isEditMutating}
				maxWidth='lg'
				fullWidth
				onClose={() => setUserAction(null)}
			>
				<SkcDeviceGroupForm id={id} onSubmit={async (data) => await triggerEdit(data)} />
			</FormDialog>
			<ConfirmationDialog
				subTitle={t('thisWillDeleteResourceWithName', {
					resource: t('deviceGroup'),
					name: userAction?.label ?? userAction?.id,
				})}
				open={userAction?.type === 'delete'}
				loading={isDeleteMutating}
				onClose={() => setUserAction(null)}
				onConfirm={async () => await triggerDelete()}
			/>
		</>
	);
};

export default SkcDeviceGroupDetailsContainer;
